import { LambdaAgent } from '../types/lambda';
import { FilterOption } from '../types/common';
import { invokeLambdaWithRetry } from './lambdaClient';

// Agent interface for the app
export interface Agent {
  id: string;
  name: string;
  description: string;
  industry: string;
  solutionName: string;
  keywords: string[];
  awsServices: string[];
  keyFeatures: string[];
  benefits: string[];
  lineOfBusiness: string;
  targetPersonas: string[];
  category: string;
  domain: string;
}

// Sanitize string for use in URL
const sanitizeForUrl = (str: string): string => {
  return str
    .toLowerCase()
    .replaceAll(/[()[\]{}]/g, '')      // Remove brackets/parens
    .replaceAll(/[/\\]/g, '-')          // Replace slashes with dash
    .replaceAll(/[^a-z0-9-]/g, '-')     // Replace other special chars with dash
    .replaceAll(/-+/g, '-')             // Collapse multiple dashes
    .replaceAll(/(^-)|(-$)/g, '');      // Trim leading/trailing dashes
};

// Map Lambda agent to Agent interface
const mapLambdaAgentToAgent = (lambdaAgent: LambdaAgent): Agent => {
  // Create a unique ID from domain + agent_name, sanitized for URLs
  const id = sanitizeForUrl(`${lambdaAgent.domain}-${lambdaAgent.agent_name}`);
  
  // Parse keywords (comma-separated)
  const keywords = lambdaAgent.keywords
    ? lambdaAgent.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
    : [];
  
  // Parse AWS services (bullet points starting with •)
  const awsServices = lambdaAgent.aws_services
    ? lambdaAgent.aws_services.split('•').map(s => s.trim()).filter(s => s.length > 0)
    : [];
  
  // Parse key features (bullet points starting with •)
  const keyFeatures = lambdaAgent.key_features
    ? lambdaAgent.key_features.split('•').map(f => f.trim()).filter(f => f.length > 0)
    : [];
  
  // Parse benefits (numbered list)
  const benefits = lambdaAgent.benefits
    ? lambdaAgent.benefits.split(/\d+\./).map(b => b.trim()).filter(b => b.length > 0)
    : [];
  
  // Parse target personas (bullet points starting with •)
  const targetPersonas = lambdaAgent.target_personas
    ? lambdaAgent.target_personas.split('•').map(p => p.trim()).filter(p => p.length > 0)
    : [];

  return {
    id,
    name: lambdaAgent.agent_name,
    description: lambdaAgent.agent_description,
    industry: lambdaAgent.target_industry,
    solutionName: lambdaAgent.solution_name,
    keywords,
    awsServices,
    keyFeatures,
    benefits,
    lineOfBusiness: lambdaAgent.line_of_business,
    targetPersonas,
    category: lambdaAgent.agent_category,
    domain: lambdaAgent.domain,
  };
};

export class AgentService {
  // Get all agents from Lambda API
  static async getAllAgents(): Promise<Agent[]> {
    try {
      const response = await invokeLambdaWithRetry<LambdaAgent[]>({
        action: 'getAllAgents',
        payload: {}
      });

      if (response.success && response.data) {
        return response.data.map(mapLambdaAgentToAgent);
      }

      console.error('Failed to fetch agents:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching all agents:', error);
      return [];
    }
  }

  // Get filter options for agents
  static getFilterOptions(agents: Agent[]): FilterOption[] {
    // Get unique industries
    const industriesSet = new Set<string>();
    for (const a of agents) {
      if (a.industry) {
        industriesSet.add(a.industry);
      }
    }
    const industries = Array.from(industriesSet).sort((a, b) => a.localeCompare(b));

    // Get unique categories
    const categoriesSet = new Set<string>();
    for (const a of agents) {
      if (a.category) {
        categoriesSet.add(a.category);
      }
    }
    const categories = Array.from(categoriesSet).sort((a, b) => a.localeCompare(b));

    const filterOptions: FilterOption[] = [
      ...industries.map(industry => ({
        id: `industry-${industry}`,
        label: industry,
        category: 'industry' as const,
        count: agents.filter(a => a.industry === industry).length
      })),
      ...categories.map(category => ({
        id: `category-${category}`,
        label: category,
        category: 'solutionType' as const,
        count: agents.filter(a => a.category === category).length
      }))
    ];

    return filterOptions;
  }
}
